import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2Icon } from '@/shared/ui/icons';
import { Input } from '@/shared/ui/inputs/Input';
import { Select } from '@/shared/ui/selects/Select';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Section } from '@/shared/ui/layout/Section';
import { formatUsd } from '@/shared/utils/currency';
import { pickLocalized } from '@/shared/utils/localized';
import type { ApiCategoriesGetItem } from '@/shared/types/api/categories';
import {
  productTotal,
  type DraftProduct,
  type ProductErrors,
} from '@/features/declaration/modules/types';

const localizedName = (item: object, lang: string): string => pickLocalized(item, 'name', lang);

type Props = {
  index: number;
  product: DraftProduct;
  categories: ApiCategoriesGetItem[];
  canRemove: boolean;
  /** Field errors from the last Save attempt (red border + message). */
  errors?: ProductErrors;
  onChange: (next: DraftProduct) => void;
  onRemove: () => void;
};

export const FeaturesDeclarationProductFormItem = ({
  index,
  product,
  categories,
  canRemove,
  errors,
  onChange,
  onRemove,
}: Props) => {
  const { t, i18n } = useTranslation('declaration');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'uz').slice(0, 2);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: localizedName(c, lang) })),
    [categories, lang],
  );

  const subcategoryOptions = useMemo(() => {
    const found = categories.find((c) => c.id === product.category_id);
    if (!found) return [];
    return found.subcategories.map((s) => ({ value: s.id, label: localizedName(s, lang) }));
  }, [categories, product.category_id, lang]);

  const total = productTotal(product);
  const err = (field: keyof ProductErrors): string | undefined => {
    const key = errors?.[field];
    return key ? t(key) : undefined;
  };

  return (
    <Section
      title={t('product', { index: index + 1 })}
      action={
        canRemove ? (
          <IconButton label={t('remove')} variant="ghost" size="sm" onClick={onRemove}>
            <Trash2Icon className="h-4 w-4 text-red-600" />
          </IconButton>
        ) : null
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select
          label={t('category')}
          required
          placeholder={t('categoryPlaceholder')}
          options={categoryOptions}
          value={product.category_id}
          error={err('category_id')}
          onChange={(e) =>
            onChange({ ...product, category_id: e.target.value, subcategory_id: '' })
          }
        />
        <Select
          label={t('subcategory')}
          required
          placeholder={
            product.category_id
              ? t('subcategoryPlaceholder')
              : t('subcategoryPlaceholderDisabled')
          }
          options={subcategoryOptions}
          value={product.subcategory_id}
          error={err('subcategory_id')}
          onChange={(e) => onChange({ ...product, subcategory_id: e.target.value })}
          disabled={!product.category_id}
        />
      </div>
      <Input
        label={t('fullName')}
        required
        value={product.full_name}
        error={err('full_name')}
        onChange={(e) => onChange({ ...product, full_name: e.target.value })}
        placeholder={t('fullNamePlaceholder')}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={t('quantity')}
          required
          type="number"
          inputMode="numeric"
          min={1}
          value={product.quantity}
          error={err('quantity')}
          onChange={(e) => onChange({ ...product, quantity: e.target.value })}
        />
        <Input
          label={t('unitPrice')}
          required
          type="number"
          inputMode="decimal"
          step="0.01"
          min={0}
          value={product.price_per_item}
          error={err('price_per_item')}
          onChange={(e) => onChange({ ...product, price_per_item: e.target.value })}
          suffix={<span className="text-xs">USD</span>}
        />
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
        <span className="text-ink-muted">{t('common:total')}</span>
        <span className="font-semibold text-ink">{formatUsd(total)}</span>
      </div>
    </Section>
  );
};
